import React, {FC, Fragment, MouseEvent, useRef, useState} from 'react'
import Analytics from '@lib/Analytics'
import {formatDateNoYear} from '@lib/format-date'
import {MiniPopup} from '@components/Popup'
import {Button} from '@components/Button'
import {CalendarCheckedIcon, CompareQuotesIcon, MobilePhoneRingingIcon,} from '@components/Icons'
import {
  Footer,
  List,
  ListItem,
  ListItemSubTitle,
  ListItemText,
  ListItemTitle,
  MarginTopDiv,
  Subtitle,
  takeQuizBtnStyles,
  Title
} from './PostLeadPopup.elements'
import {LeadgenPopupType} from './LeadgenPopupFlow'

type Props = {
  answersPreset: Record<string, any>
  onClose: () => void
  small: boolean
  industryType?: IndustryType
  leadType: LeadgenPopupType
  customerId?: string
  consultationDate?: string
  bestTimeToCall?: 'morning' | 'afternoon/evening'
}

export const PostLeadPopup: FC<Props> = ({
  onClose,
  small,
  industryType,
  leadType,
  customerId,
  consultationDate,
  bestTimeToCall,
  answersPreset,
}) => {
  const popupRef = useRef<MiniPopup>(null)
  const [isWaiting, setIsWaiting] = useState<boolean>(false)

  const getUrl = () => {
    if (leadType === LeadgenPopupType.AUTO_INSURANCE) {
      let redirectUrl = '/car-insurance/special-rate-availability?'

      if (answersPreset.address_zip) {
        redirectUrl += `zipcode=${answersPreset.address_zip}&`
      }
      if (answersPreset.email) {
        redirectUrl += `email=${encodeURIComponent(answersPreset.email)}&`
      }
      if (answersPreset.age) {
        redirectUrl += `age=${answersPreset.age}&`
      }

      redirectUrl = redirectUrl.substring(0, redirectUrl.length - 1)

      return redirectUrl;
    }

    if (leadType === LeadgenPopupType.MEDADV ||
        leadType === LeadgenPopupType.MEDSUP ||
        leadType === LeadgenPopupType.SCHEDULE_AEP){
      return '/quiz-center/65-preliminary-health-iq/play';
    } else {
      return '/quiz/preliminary-health-iq-quiz/play'
    }
  };

  const handleTakeQuiz = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    setIsWaiting(true)
    Analytics.log('Click', { location: 'start-quiz' })

    setTimeout(() => {
      window.location.href = getUrl()
    }, 800)
  }

  const isMedsupOrMedAdv = (leadType === LeadgenPopupType.MEDADV || leadType === LeadgenPopupType.MEDSUP)
  const isScheduling = (leadType === LeadgenPopupType.SCHEDULE_AEP)

  const quizText = isMedsupOrMedAdv
    ? 'Take the quiz and see if you qualify for additional Medicare Supplement savings.'
    : 'Take the quiz and see if you qualify for additional savings.'

  const title = isMedsupOrMedAdv
    ? 'Great! Let’s finalize & lock in your coverage.'
    : isScheduling
      ? 'Thank you!'
      : 'Great! Let’s go through your options & lock in your plan details.'

  const consultationDateText = consultationDate ? formatDateNoYear(new Date(consultationDate)) : ''

  const listItemSubtitle = leadType === 'AUTO_INSURANCE' ? 'SHARP SENIOR QUIZ' : 'HEALTH IQ QUIZ';

  return (
    <MiniPopup
      onClose={onClose}
      ref={popupRef}
    >
      <Title>{title}</Title>

      { customerId &&
        <Subtitle>Your reference ID is: {customerId}</Subtitle>
      }

      <List>
        {!isScheduling && (
          <Fragment>
            <ListItem>
              <MobilePhoneRingingIcon />
              <div>
                <ListItemTitle>YOUR NEXT STEP:</ListItemTitle>
                <ListItemSubTitle> PICK UP THE PHONE</ListItemSubTitle>
                <ListItemText>
                  Your licensed insurance agent will call you shortly to walk you through your options and help secure your coverage.
                </ListItemText>
              </div>
            </ListItem>

            {!small && (
              <ListItem>
                <CompareQuotesIcon />
                <div>
                  <ListItemTitle>UNLOCK SAVINGS </ListItemTitle>
                  <ListItemSubTitle>{listItemSubtitle}</ListItemSubTitle>
                  <ListItemText>
                    {quizText}
                  </ListItemText>
                </div>
              </ListItem>
            )}
          </Fragment>
        )}

        {isScheduling && (
          <ListItem>
            <CalendarCheckedIcon />
            <ListItemText>
              We look forward to connecting with you about your Medicare plan options & Open Enrollment rates and benefits <strong>the {bestTimeToCall} of {consultationDateText}.</strong>
              <br /><br />
            </ListItemText>
          </ListItem>
        )}
      </List>

      {!small && !isScheduling && (
        <MarginTopDiv>
          <Button
            variant="green-wide"
            onClick={handleTakeQuiz}
            css={takeQuizBtnStyles}
            disabled={isWaiting}
          >
            {isWaiting ? 'Loading...' : 'Take the Quiz'}
          </Button>

          { isMedsupOrMedAdv && (
            <Footer>
              No underwriting applies during Medicare Supplement open enrollment or guaranteed issue periods.
            </Footer>
          )}
        </MarginTopDiv>
      )}
    </MiniPopup>
  )
}
